import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useAcceptTeamInvitationMutation,
  useGetTeamByIdQuery,
  useGetTeamAvatarQuery,
  useGetTeamInvitationByInvitationIdQuery,
  useRejectTeamInvitationMutation,
} from "marketplace/teams/_api/marketplace-team-api";
import {
  getTeamById,
  getTeamInvitationById,
} from "marketplace/teams/_store/marketplace-teams-redux-slice";
import ViewTeam from "marketplace/teams/_views/view-team";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";

const PageContainer = styled("div")(({ theme }) => ({
  padding: theme.spacing(4),
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  marginBottom: theme.spacing(5),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const HeaderInnerContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    justifyItems: "center",
  },
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "min-content auto",
    gridTemplateRows: "min-content auto",
  },
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "min-content auto 1fr",
  },
  alignItems: "flex-start",
  gap: theme.spacing(2),
  display: "grid",
  width: "100%",
}));
const TitleContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  marginTop: theme.spacing(1),
  "& > *:not(:last-child)": {
    marginRight: theme.spacing(2),
  },
}));
const StyledAvatar = styled(Avatar)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    height: theme.spacing(7),
    width: theme.spacing(7),
  },
  height: theme.spacing(15),
  width: theme.spacing(15),
}));
const ProfileTypeChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.text.disabled,
  margin: theme.spacing(0.25),
}));
const NameChipContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  alignItems: "flex-start",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "flex-start",
}));
const DescriptionContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(1, 0),
    width: "100%",
  },
  [theme.breakpoints.up("sm")]: {
    gridColumn: "1 / span 2",
    gridRow: "2",
    margin: theme.spacing(2, 0),
  },
  [theme.breakpoints.up("lg")]: {
    gridColumn: "3",
    gridRow: "1",
    margin: 0,
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "space-between",
  margin: 0,
  textAlign: "left",
  width: "100%",
}));
const DescriptionText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    textAlign: "center",
  },
  fontSize: "1.1em",
  fontStyle: "italic",
  lineHeight: "1.1em",
}));
const DescriptionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
}));
const AcceptButton = styled(LoadingButton)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  minWidth: theme.spacing(18),
  "&:hover": {
    backgroundColor: theme.palette.success.dark,
  },
}));
const RejectButton = styled(LoadingButton)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  minWidth: theme.spacing(18),
  "&:hover": {
    backgroundColor: theme.palette.error.dark,
  },
}));

export default function TeamInvitationPage() {
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const returnUrl = "/marketplace/profiles/teams";

  const session = useSession();
  const params = useParams();
  const invitationIdValue = params.invitationId;
  const navigate = useNavigate();

  const [acceptTeamInvitation] = useAcceptTeamInvitationMutation();
  const [rejectTeamInvitation] = useRejectTeamInvitationMutation();

  const invitationId = useMemo(
    () => (invitationIdValue ? new Guid(invitationIdValue) : undefined),
    [invitationIdValue],
  );

  const invitationQuery = useGetTeamInvitationByInvitationIdQuery(
    invitationId ? { invitationId: invitationId.value } : skipToken,
  );
  const invitationSelector = useMemo(
    () => (invitationId ? getTeamInvitationById(invitationId) : () => undefined),
    [invitationId],
  );
  const invitation = useSelector(invitationSelector);
  const { isLoading: loadingInvitation, error: invitationLoadingError } = invitationQuery;

  const teamId = invitation?.teamId;
  const teamQuery = useGetTeamByIdQuery(teamId ? { teamId: teamId.value } : skipToken);
  const teamSelector = useMemo(
    () => (teamId ? getTeamById(teamId) : () => undefined),
    [teamId],
  );
  const team = useSelector(teamSelector);
  const { isLoading: loadingTeam, error: teamLoadingError } = teamQuery;

  const { data: avatar } = useGetTeamAvatarQuery(team?.id ? { teamId: team.id.value, size: "small" } : skipToken);

  useEffect(() => {
    if (invitationLoadingError) {
      enqueueSnackbar("Error loading invitation. Please try again later.", { variant: "error" });
    }
    if (teamLoadingError) {
      enqueueSnackbar("Error loading team. Please try again later.", { variant: "error" });
    }
  }, [invitationLoadingError, teamLoadingError]);

  const isInvitationTarget = useMemo(
    () => invitation?.inviteeId.isEqualTo(session.user?.individualId),
    [invitation?.inviteeId, session.user?.individualId],
  );

  async function acceptInvitation() {
    try {
      setAccepting(true);
      if (!invitationId) throw new Error("Invitation ID was not provided");
      if (!teamId) throw new Error("Team ID was not available for invitation acceptance");
      await acceptTeamInvitation({ invitationId: invitationId.value, teamId: teamId.value }).unwrap();
      enqueueSnackbar("Accepted invitation to team", { variant: "success" });
      navigate(returnUrl);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't accept invitation. Please try again", {
        variant: "error",
      });
    } finally {
      setAccepting(false);
    }
  }

  async function rejectInvitation() {
    try {
      setRejecting(true);
      if (!invitationId) throw new Error("Invitation ID was not provided");
      if (!teamId) throw new Error("Team ID was not available for invitation rejection");
      await rejectTeamInvitation({ invitationId: invitationId.value, teamId: teamId.value }).unwrap();
      enqueueSnackbar("Rejected invitation to vendor team", { variant: "info" });
      navigate(returnUrl);
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Couldn't reject invitation. Please try again", {
        variant: "error",
      });
    } finally {
      setRejecting(false);
    }
  }

  const isLoading = loadingInvitation || loadingTeam;

  return (
    <PageContainer>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <HeaderContainer>
            <HeaderInnerContainer>
              <StyledAvatar src={avatar?.toString()} />
              <TitleContainer>
                <NameChipContainer>
                  {!invitation?.closed && !isInvitationTarget && (
                    <Typography variant="h4">Invitation to Join {team?.profile?.name} Team</Typography>
                  )}
                  {!invitation?.closed && isInvitationTarget && (
                    <Typography variant="h4">Join {team?.profile?.name} Team?</Typography>
                  )}
                  {invitation?.closed && <Typography variant="h4">Invitation to join {team?.profile?.name}</Typography>}
                  <span>
                    {team?.profile?.categories?.map((category: string) => (
                      <ProfileTypeChip key={category} label={category} size="small" />
                    ))}
                  </span>
                </NameChipContainer>
                {!invitation?.closed && isInvitationTarget && (
                  <ButtonContainer>
                    <AcceptButton
                      variant="contained"
                      startIcon={<CheckIcon />}
                      disabled={accepting || rejecting}
                      loading={accepting}
                      onClick={acceptInvitation}
                    >
                      Accept
                    </AcceptButton>
                    <RejectButton
                      variant="contained"
                      startIcon={<CloseIcon />}
                      disabled={accepting || rejecting}
                      loading={rejecting}
                      onClick={rejectInvitation}
                    >
                      Reject
                    </RejectButton>
                  </ButtonContainer>
                )}
                {!invitation?.closed && !isInvitationTarget && (
                  <Typography variant="h6">This invitation is pending acceptance.</Typography>
                )}
                {invitation?.acceptedAt && (
                  <Typography variant="h6">Accepted on {invitation?.acceptedAt.format("MM/DD/YYYY")}</Typography>
                )}
                {invitation?.closed && !invitation?.acceptedAt && (
                  <Typography variant="h6">Invitation no longer available.</Typography>
                )}
              </TitleContainer>
              <DescriptionContainer>
                <DescriptionText variant="subtitle1">{team?.profile?.description}</DescriptionText>
                <DescriptionDivider />
              </DescriptionContainer>
            </HeaderInnerContainer>
          </HeaderContainer>
          {invitation?.message && (
            <>
              <Typography variant="h5">Message from {team?.leader?.fullName}:</Typography>
              <Typography>{invitation?.message}</Typography>
            </>
          )}
          <ViewTeam team={team} />
        </>
      )}
    </PageContainer>
  );
}
